import type { ChangeEvent } from "react";
import { Eye } from "lucide-react";
import { Select } from "@marekl/adminer/components";
import { useSeason } from "../../season";
import styles from "./SeasonSwitcher.module.css";

interface SeasonSwitcherProps {
  // Most pages that follow the selection can only WRITE into the active
  // season, so a non-active choice is a read-only historical view and the
  // chip says so. A page that legitimately writes outside the active season
  // — the competition calendar, built before its season goes active —
  // passes this to suppress the marker: „jen pro čtení" next to a working
  // „Nová soutěž" button is worse than no marker at all.
  //
  // It suppresses the marker for a FUTURE season only. A season that is
  // already over takes it back: the calendar is built ahead of its season,
  // never behind it, and the server refuses a competition in a closed one
  // (competition.ErrSeasonInPast). So even the writable pages are read-only
  // in the past, and the marker must say so rather than let somebody fill
  // in a form the save will reject.
  writable?: boolean;
}

// SeasonSwitcher is the season selector shown in the topbar of the pages
// that opt into it. It defaults to the active season; picking any other
// season puts those pages in read-only historical view (writes target the
// active season, unless the page says otherwise via `writable`).
export function SeasonSwitcher({ writable = false }: SeasonSwitcherProps) {
  const {
    seasons,
    activeSeason,
    selectedSeason,
    isActiveSelected,
    isSelectedPast,
    setSelectedSeasonId,
  } = useSeason();

  if (seasons.length === 0) return null;

  const readOnly = !isActiveSelected && (!writable || isSelectedPast);

  function handleChange(e: ChangeEvent<HTMLSelectElement>) {
    const id = e.target.value;
    // Selecting the active season clears the explicit choice so the app
    // keeps following "active" as it changes over time.
    if (activeSeason && id === activeSeason.id) {
      setSelectedSeasonId(null);
    } else {
      setSelectedSeasonId(id);
    }
  }

  return (
    <div className={`${styles.switcher} ${readOnly ? styles.readonlyMode : ""}`}>
      <span className={styles.label} aria-hidden>
        Sezóna
      </span>
      <Select
        aria-label="Sezóna"
        className={styles.select}
        value={String(selectedSeason?.id ?? "")}
        onChange={handleChange}
      >
        {seasons.map((s) => (
          <option key={s.id} value={String(s.id)}>
            {s.code}
          </option>
        ))}
      </Select>
      {readOnly && (
        <span
          className={styles.eye}
          role="img"
          aria-label="Jen pro čtení"
          title="Historickou sezónu nelze upravovat"
        >
          <Eye size={16} aria-hidden />
        </span>
      )}
    </div>
  );
}
